import { pbjExportActions } from '../../actions/constant';

const {
  GET_PBJ_EXPORT_LOADING,
  GET_PBJ_EXPORT_SUCCESS,
  GET_PBJ_EXPORT_FAILURE,
  GET_PBJ_EXPORT_RESET,
} = pbjExportActions;

/**
 * RA-2027 — the PBJ export result.
 *
 * `warningCount` / `warnings` sit at the top level rather than inside `response` because they
 * arrive on the 404 as well as the 200, and the 404 has no response body worth keeping.
 *
 * `isNoData` and `isError` are mutually exclusive: a 404 carrying PBJ_NO_DATA_FOR_QUARTER means
 * the quarter is empty, which is an empty state and not a failure. Collapsing the two would put an
 * error toast in front of a customer whose only problem is that they picked a quarter before they
 * went live.
 *
 * Not in the redux-persist whitelist — `response.data` is the file's bytes.
 */
type PbjExportState = {
  loading: boolean;
  response: any;
  isSuccess: boolean;
  isError: boolean;
  isNoData: boolean;
  isTimeout: boolean;
  format: string | null;
  warningCount: number;
  warnings: Record<string, number>;
  detail: string | null;
};

//  Typed rather than the repo's usual `object`, because this reducer's tests read properties off
//  the returned state. `tsconfig.json` on develop type-checks test files, so `object` there costs
//  36 `tsc` errors and fails `npm run build:qa` before vite ever runs.
const initialState: PbjExportState = {
  loading: false,
  response: null,
  isSuccess: false,
  isError: false,
  isNoData: false,
  isTimeout: false,
  format: null,
  warningCount: 0,
  warnings: {},
  //  Present in the initial state so every branch returns the same shape; the reducer sets it on
  //  the failure and no-data paths.
  detail: null,
};

export const getPbjExportReducer = (state = initialState, action: any) => {
  switch (action.type) {
    case GET_PBJ_EXPORT_LOADING:
      //  Back to a clean slate, not a merge: warnings and messages belong to the request that
      //  produced them, and showing last quarter's counts against this quarter's file is worse
      //  than showing none.
      return {
        ...initialState,
        loading: true,
      };

    case GET_PBJ_EXPORT_SUCCESS:
      return {
        ...state,
        loading: false,
        response: action.payload?.download ?? null,
        isSuccess: true,
        isError: false,
        isNoData: false,
        isTimeout: false,
        format: action.payload?.format ?? null,
        warningCount: action.payload?.warningCount ?? 0,
        warnings: action.payload?.warnings ?? {},
      };

    case GET_PBJ_EXPORT_FAILURE: {
      const isNoData = Boolean(action.payload?.isNoData);
      return {
        ...state,
        loading: false,
        response: null,
        isSuccess: false,
        isError: !isNoData,
        isNoData,
        isTimeout: Boolean(action.payload?.isTimeout),
        warningCount: action.payload?.warningCount ?? 0,
        warnings: action.payload?.warnings ?? {},
      };
    }

    case GET_PBJ_EXPORT_RESET:
      return initialState;

    default:
      return state;
  }
};
